import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import {
  Container,
  Card,
  Stack,
  Typography,
  Button,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Box,
  Tooltip,
} from "@mui/material";
import Iconify from "../../components/iconify";

import SmartTable from "../../components/SmartTable/SmartTable";
const EMPTY_FORM = { name: "", stamp: "" };

export default function Welders() {
  const [welders, setWelders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, obj = edit
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchWelders = async () => {
    try {
      setLoading(true);
      const res = await ApiCall.get("/welders", { params: { search } });
      setWelders(res.data?.data || res.data || []);
    } catch (err) {
      console.error("fetchWelders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWelders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleOpen = (welder = null) => {
    setEditing(welder);
    setFormData(welder ? { name: welder.name, stamp: welder.stamp } : EMPTY_FORM);
    setOpenForm(true);
  };

  const handleClose = () => {
    setOpenForm(false);
    setEditing(null);
    setFormData(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.stamp.trim()) return;
    setSubmitLoading(true);
    try {
      if (editing) {
        await ApiCall.put(`/welders/${editing.id}`, formData);
      } else {
        await ApiCall.post("/welders", formData);
      }
      handleClose();
      fetchWelders();
    } catch (err) {
      console.error("saveWelder:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ApiCall.delete(`/welders/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchWelders();
    } catch (err) {
      console.error("deleteWelder:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Welders | WeldAssure</title>
      </Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Welders</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleOpen()}
          >
            Add Welder
          </Button>
        </Stack>
        <PageGuide
          why="Keep your welder roster with each welder's qualifications in one place."
          who="The QM and supervisors, maintaining the welder roster and quals."
          what="Your list of welders and the procedures they're qualified for."
          how="Add a welder and link their qualification test records (WQTR)."
          when="When a welder joins, and as their qualifications are tested or renewed."
          standard="AWS D1.1 §4.18"
        />

        <Card>
          <Box sx={{ p: 2 }}>
            <TextField
              size="small"
              placeholder="Search name or stamp…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 280 }}
            />
          </Box>

          <TableContainer component={Paper} elevation={0}>
            <SmartTable tableId="welders" lockedColumns={["Name", "Stamp / ID"]}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Stamp / ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : welders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No welders found
                    </TableCell>
                  </TableRow>
                ) : (
                  welders.map((w) => (
                    <TableRow key={w.id} hover>
                      <TableCell>{w.name}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: "grey.100",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            display: "inline-block",
                          }}
                        >
                          {w.stamp}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpen(w)}>
                            <Iconify icon="eva:edit-fill" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(w)}>
                            <Iconify icon="eva:trash-2-fill" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </SmartTable>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={openForm} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? "Edit Welder" : "Add Welder"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Full Name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Stamp / ID"
              value={formData.stamp}
              onChange={(e) => setFormData((p) => ({ ...p, stamp: e.target.value }))}
              fullWidth
              required
              helperText="Unique welder identifier (e.g. JD-87)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={submitLoading || !formData.name.trim() || !formData.stamp.trim()}
          >
            {submitLoading ? "Saving…" : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Welder?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteTarget?.name}</strong> (stamp:{" "}
            <strong>{deleteTarget?.stamp}</strong>) from the directory?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
